import { CustomWindow } from "../custom"
import { CartStructure, isRentalDataPlanVariant, Product, Variant } from "../components/interfaces/SaleorTypes"
import { PRODUCT_RENTAL_TERMINAL_SLUG } from "./saleor-constants"

declare let window: CustomWindow

const TERMINALS_CATEGORY = "Terminals"
const DATA_PLANS_CATEGORY = "Data plans"
const EXTENDED_WARRANTY_CATEGORY = "Terminal Warranties"
const categoriesNotToTrack = [DATA_PLANS_CATEGORY, EXTENDED_WARRANTY_CATEGORY]

// form names
export const FORM_NAME_LATEST_NEWS = "Latest News"
export const FORM_NAME_CONTACT_SALES = "Contact Sales"
export const FORM_NAME_REQUST_DEMO = "Request Demo"
export const FORM_NAME_DOWNLOAD_KU = "Product Sheet Ku"
export const FORM_NAME_DOWNLOAD_SECURE = "Product Sheet Secure"
export const FORM_NAME_DOWNLOAD_RENTAL = "Rental Service Specification"
export const FORM_NAME_DOWNLOAD_MOTION = "Product Sheet Motion"
export const FORM_NAME_MOTION_SIGNUP = "Motion Signup"

type PiwikItem = {
  item_id: string
  item_name: string
  item_category: string
  quantity: number
  price: number
  extended_warranty?: number
  data_plan?: string
}

const createPiwikItems = (products: CartStructure[]): PiwikItem[] => {
  const hasTerminals = products.some((item) => item.product.category.name === TERMINALS_CATEGORY)
  const hasDataPlans = products.some((item) => item.product.category.name === DATA_PLANS_CATEGORY)
  const isSolution = hasTerminals && hasDataPlans

  return products
    .filter((item) => !categoriesNotToTrack.includes(item.product.category.name))
    .map((item) => {
      const product = item.product
      const variant = item.product.variants[0]
      const itemName = getItemName(product, variant)

      return {
        item_id: product.id,
        item_name: itemName,
        item_category: getItemCategory(isSolution, product),
        quantity: item.amount,
        price: variant?.pricing.price.gross.amount,
        extended_warranty: getExtendedWarranty(isSolution, product, products),
        data_plan: getDataPlan(isSolution, product, products),
      }
    })
}

const getItemName = (product: Product, variant: Variant): string => {
  if (product.slug === PRODUCT_RENTAL_TERMINAL_SLUG) {
    return `${product.name} - ${variant.name}`
  } else if (isRentalDataPlanVariant(variant)) {
    return `${product.name} ${variant.dataPackageGB} GB`
  } else {
    return product.name
  }
}

const getItemCategory = (isSolution: boolean, product: Product): string => {
  return isSolution && product.category.name === TERMINALS_CATEGORY ? "Solutions" : product.category.name
}

const getExtendedWarranty = (isSolution: boolean, product: Product, products: CartStructure[]): number | undefined => {
  return product.category.name === TERMINALS_CATEGORY && isSolution
    ? products.find((x) => x.product.category.name === EXTENDED_WARRANTY_CATEGORY)?.amount
    : undefined
}

const getDataPlan = (isSolution: boolean, product: Product, products: CartStructure[]): string | undefined => {
  return product.category.name === TERMINALS_CATEGORY && isSolution
    ? // find the item in the products that is a category of data plan
      products.find((x) => x.product.category.name === DATA_PLANS_CATEGORY)?.product.variants[0].name
    : undefined
}

export const pushAddToCartEvent = (products: CartStructure[]) => {
  window.dataLayer = window.dataLayer || []

  const currency = products[0]?.product.variants[0].pricing.price.gross.currency
  window.dataLayer.push({
    event: "add_to_cart",
    ecommerce: {
      currency: currency,
      items: createPiwikItems(products),
    },
  })
}

export const pushPurchaseEvent = (transactionId: string, order: any, cart?: CartStructure[]) => {
  window.dataLayer = window.dataLayer || []

  const currency = cart ? cart[0]?.product.variants[0].pricing.price.currency : undefined
  window.dataLayer.push({
    event: "purchase",
    ecommerce: {
      currency: currency,
      transaction_id: transactionId,
      value: order.total?.gross?.amount,
      subtotal: order.subtotal?.gross?.amount,
      tax: order.total?.tax?.amount,
      shipping: order.shippingPrice?.gross?.amount,
      discount: order.undiscountedTotal?.gross?.amount - order?.total?.gross?.amount,
      items: cart ? createPiwikItems(cart) : undefined,
    },
  })
}

export const pushFormSubmitEvent = (formName: string) => {
  window.dataLayer = window.dataLayer || []

  window.dataLayer.push({
    event: "form_submission",
    form_name: formName,
  })
}
